import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, ArrowRight, Newspaper, AlertTriangle, ChevronLeft, ChevronRight, Pin } from 'lucide-react'
import { useLanguage } from '@/localization'
import { useNews, formatDate, itemDate, type NewsItem, type NewsTag } from '@/useNews'
import { newsTagLabel } from '../../../shared/localization/core'

/** Visual style per tag chip (light theme). */
const tagStyles: Record<string, string> = {
  general: 'bg-uliunai-red/10 text-uliunai-red ring-uliunai-red/25',
  kf1: 'bg-uliunai-red/10 text-uliunai-red ring-uliunai-red/25',
  kf2: 'bg-stone-200 text-stone-600 ring-stone-300',
  minecraft: 'bg-green-600/10 text-green-700 ring-green-600/25',
}

function tagClass(tag?: NewsTag): string {
  if (!tag) return tagStyles.general
  return tagStyles[tag.toLowerCase()] ?? tagStyles.general
}

/** A small tag chip. */
function TagChip({ tag }: { tag?: NewsTag }) {
  const { t } = useLanguage()

  if (!tag) return null
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 font-rajdhani text-xs font-semibold uppercase tracking-wider ring-1 ${tagClass(tag)}`}
    >
      {t(newsTagLabel(String(tag)))}
    </span>
  )
}

/**
 * Community News teaser. Fetches hub/general posts only; game-specific posts are
 * shown on their own sites through the same platform news API.
 *
 * Layout: a FEATURED slot (pinned posts — a carousel if more than one, the most
 * recent post if none are pinned) followed by the latest 3 posts not already in
 * the featured slot, and a "View all news →" link to the dedicated /news page.
 */
export default function NewsFeed() {
  const state = useNews()

  return (
    <section id="news" className="scroll-mt-20 px-6 py-20">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          icon={<Newspaper size={18} />}
          eyebrow="Latest updates"
          title="Community News"
        />

        <div className="mt-10">
          {state.status === 'loading' && <NewsSkeleton />}
          {state.status === 'error' && <NewsError />}
          {state.status === 'ready' &&
            (state.items.length === 0 ? <NewsEmpty /> : <NewsTeaser items={state.items} />)}
        </div>
      </div>
    </section>
  )
}

/** The featured + latest-3 + view-all composition once items are loaded. */
function NewsTeaser({ items }: { items: NewsItem[] }) {
  const { t } = useLanguage()
  // Featured = pinned posts; if none are pinned, the single most-recent post.
  const pinned = items.filter((i) => i.pinned)
  const featured = pinned.length > 0 ? pinned : items.slice(0, 1)
  const featuredIds = new Set(featured.map((i) => i.id))
  // Latest = next 3 most-recent posts not already shown in the featured slot.
  const latest = items.filter((i) => !featuredIds.has(i.id)).slice(0, 3)

  return (
    <>
      {featured.length > 1 ? (
        <FeaturedCarousel items={featured} />
      ) : (
        featured[0] && <FeaturedCard item={featured[0]} />
      )}

      {latest.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {latest.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      )}

      <div className="mt-10 text-center">
        <Link
          to="/news"
          className="inline-flex items-center gap-1.5 rounded-full border border-stone-line bg-white px-5 py-2.5 font-rajdhani text-sm font-bold uppercase tracking-wider text-uliunai-red shadow-card transition-all hover:-translate-y-0.5 hover:border-uliunai-red/30 hover:shadow-card-hover"
        >
          {t('View all news')}
          <ArrowRight size={16} />
        </Link>
      </div>
    </>
  )
}

/** Hand-rolled carousel (prev/next arrows + dots) for multiple pinned posts. */
function FeaturedCarousel({ items }: { items: NewsItem[] }) {
  const { t } = useLanguage()
  const [index, setIndex] = useState(0)
  const count = items.length
  const current = items[index]
  const go = (next: number) => setIndex((next + count) % count)

  return (
    <div className="relative">
      <FeaturedCard item={current} />

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label={t('Previous featured post')}
        className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full border border-stone-line bg-white/90 text-stone-700 shadow-card backdrop-blur transition-colors hover:border-uliunai-red/40 hover:text-uliunai-red"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label={t('Next featured post')}
        className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full border border-stone-line bg-white/90 text-stone-700 shadow-card backdrop-blur transition-colors hover:border-uliunai-red/40 hover:text-uliunai-red"
      >
        <ChevronRight size={20} />
      </button>

      <div className="mt-4 flex items-center justify-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`${t('Go to featured post')} ${i + 1}`}
            aria-current={i === index}
            className={`h-2.5 rounded-full transition-all ${
              i === index ? 'w-6 bg-uliunai-red' : 'w-2.5 bg-stone-300 hover:bg-stone-400'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

/** Large featured post card, linking to its detail page. */
function FeaturedCard({ item }: { item: NewsItem }) {
  const { t } = useLanguage()
  const date = itemDate(item)

  return (
    <Link
      to={`/news/${item.id}`}
      className="group block rounded-2xl border border-stone-line bg-white p-7 shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-uliunai-red/30 hover:shadow-card-hover sm:p-9"
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {item.pinned && (
          <span className="inline-flex items-center gap-1 rounded-full bg-uliunai-red px-2.5 py-1 font-rajdhani text-xs font-bold uppercase tracking-wider text-white">
            <Pin size={12} />
            {t('Featured')}
          </span>
        )}
        <TagChip tag={item.tag} />
        {date && (
          <time dateTime={date} className="font-rajdhani text-xs font-medium text-stone-500">
            {formatDate(date)}
          </time>
        )}
      </div>

      <h3 className="font-orbitron text-2xl font-black leading-tight text-stone-900 sm:text-3xl">
        {item.title}
      </h3>

      {item.excerpt && (
        <p className="mt-4 max-w-3xl font-rajdhani text-base leading-relaxed text-stone-600">
          {item.excerpt}
        </p>
      )}

      <span className="mt-6 inline-flex items-center gap-1 font-rajdhani text-sm font-bold uppercase tracking-wider text-uliunai-red transition-colors group-hover:text-uliunai-red-dark">
        {t('Read full update')}
        <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  )
}

/** Compact news card for the "latest" row, linking to its detail page. */
function NewsCard({ item }: { item: NewsItem }) {
  const { t } = useLanguage()
  const date = itemDate(item)

  return (
    <Link
      to={`/news/${item.id}`}
      className="group flex flex-col rounded-2xl border border-stone-line bg-white p-6 shadow-card transition-all duration-200 hover:-translate-y-1 hover:border-uliunai-red/30 hover:shadow-card-hover"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <TagChip tag={item.tag} />
        {date && (
          <time dateTime={date} className="font-rajdhani text-xs font-medium text-stone-500">
            {formatDate(date)}
          </time>
        )}
      </div>

      <h3 className="font-orbitron text-lg font-bold leading-snug text-stone-900">{item.title}</h3>

      {item.excerpt && (
        <p className="mt-3 flex-1 font-rajdhani text-sm leading-relaxed text-stone-600">
          {item.excerpt}
        </p>
      )}

      <span className="mt-5 inline-flex items-center gap-1 font-rajdhani text-sm font-semibold text-uliunai-red transition-colors group-hover:text-uliunai-red-dark">
        {t('Read more')}
        <ArrowUpRight
          size={15}
          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </span>
    </Link>
  )
}

function NewsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="animate-pulse rounded-2xl border border-stone-line bg-white p-9 shadow-card">
        <div className="mb-4 flex items-center gap-3">
          <div className="h-5 w-20 rounded-full bg-stone-200" />
          <div className="h-3 w-20 rounded bg-stone-200" />
        </div>
        <div className="mb-3 h-7 w-2/3 rounded bg-stone-200" />
        <div className="space-y-2">
          <div className="h-3 w-full rounded bg-stone-200" />
          <div className="h-3 w-4/5 rounded bg-stone-200" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="animate-pulse rounded-2xl border border-stone-line bg-white p-6 shadow-card"
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="h-5 w-16 rounded-full bg-stone-200" />
              <div className="h-3 w-20 rounded bg-stone-200" />
            </div>
            <div className="mb-3 h-5 w-3/4 rounded bg-stone-200" />
            <div className="space-y-2">
              <div className="h-3 w-full rounded bg-stone-200" />
              <div className="h-3 w-5/6 rounded bg-stone-200" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function NewsEmpty() {
  const { t } = useLanguage()

  return (
    <div className="rounded-2xl border border-dashed border-stone-300 bg-white/60 px-6 py-16 text-center">
      <Newspaper size={28} className="mx-auto mb-3 text-stone-400" />
      <p className="font-rajdhani text-lg font-medium text-stone-700">{t('No news yet')}</p>
      <p className="mt-1 font-rajdhani text-sm text-stone-500">
        {t('Check back soon for community updates and announcements.')}
      </p>
    </div>
  )
}

function NewsError() {
  const { t } = useLanguage()

  return (
    <div className="rounded-2xl border border-uliunai-red/30 bg-uliunai-red/5 px-6 py-16 text-center">
      <AlertTriangle size={28} className="mx-auto mb-3 text-uliunai-red" />
      <p className="font-rajdhani text-lg font-medium text-stone-800">
        {t("Couldn't load the news feed")}
      </p>
      <p className="mt-1 font-rajdhani text-sm text-stone-500">
        {t('Please try again in a little while.')}
      </p>
    </div>
  )
}

function SectionHeading({
  icon,
  eyebrow,
  title,
}: {
  icon: React.ReactNode
  eyebrow: string
  title: string
}) {
  const { t } = useLanguage()

  return (
    <div className="text-center">
      <span className="inline-flex items-center gap-2 rounded-full border border-stone-line bg-white px-4 py-1.5 font-rajdhani text-xs font-semibold uppercase tracking-[0.25em] text-uliunai-red shadow-card">
        {icon}
        {t(eyebrow)}
      </span>
      <h2 className="metal-text mt-5 font-orbitron text-4xl font-black tracking-tight sm:text-5xl">
        {t(title)}
      </h2>
    </div>
  )
}
